import { bindable, customElement } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { resolve } from 'aurelia';
import template from './rule-modal.html';
import type { Rule } from '../services/api.types';
import { createRule, updateRule } from '../services/api.service';

@customElement({ name: 'rule-modal', template })
export class RuleModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: (rule: Rule) => void;
  @bindable public closed!: () => void;
  @bindable public ruleToEdit: Rule | null = null;

  public title = '';
  public selector = '';
  public code = DEFAULT_CODE;
  public runBeforeMetadataExtraction = false;
  public saving = false;
  public error = '';
  public selectorError = '';

  private _keyHandler!: (e: KeyboardEvent) => void;

  public get isEditMode(): boolean {
    return this.ruleToEdit !== null;
  }

  public attached(): void {
    if (this.ruleToEdit) {
      this.title    = this.ruleToEdit.title;
      this.selector = this.ruleToEdit.selector;
      this.code     = this.ruleToEdit.code;
      this.runBeforeMetadataExtraction = this.ruleToEdit.runBeforeMetadataExtraction;
    }
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public close(): void {
    this.closed();
  }

  public validateSelector(): void {
    this.selectorError = '';
    if (!this.selector.trim()) return;
    try {
      new RegExp(this.selector);
    } catch {
      this.selectorError = this.i18n.tr('rules.form.selectorInvalid');
    }
  }

  public async save(): Promise<void> {
    this.error = '';
    this.selectorError = '';

    if (!this.title.trim()) {
      this.error = this.i18n.tr('rules.form.titleRequired');
      return;
    }
    if (!this.selector.trim()) {
      this.error = this.i18n.tr('rules.form.selectorRequired');
      return;
    }
    try {
      new RegExp(this.selector);
    } catch {
      this.selectorError = this.i18n.tr('rules.form.selectorInvalid');
      return;
    }

    this.saving = true;
    try {
      let rule: Rule;
      if (this.isEditMode && this.ruleToEdit) {
        rule = await updateRule(this.ruleToEdit.id, {
          title:    this.title.trim(),
          selector: this.selector.trim(),
          code:     this.code,
          runBeforeMetadataExtraction: this.runBeforeMetadataExtraction,
        });
      } else {
        rule = await createRule({
          title:    this.title.trim(),
          selector: this.selector.trim(),
          code:     this.code,
          runBeforeMetadataExtraction: this.runBeforeMetadataExtraction,
        });
      }
      this.saved(rule);
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }
}

// The JavaScript below is never executed in the browser — it is only ever sent to and
// run on the app server (via the rule engine) whenever a media item's captured date
// could not be determined from the file content (e.g. missing EXIF data).
const DEFAULT_CODE = `// Available variable:
//   filename - the name of the uploaded file (string)
//
// Return an object with any of these optional fields:
//   { title, capturedAt, isCapturedAtTimeAccurate, tags }
//
// capturedAt should be an ISO date/time string. Set isCapturedAtTimeAccurate to false
// when only a date (no time-of-day) could be determined from the filename.
//
// Examples:
//   // IMG_20240521_143512.jpg -> 2024-05-21T14:35:12
//   const m = filename.match(/(\\d{4})(\\d{2})(\\d{2})_(\\d{2})(\\d{2})(\\d{2})/);
//   if (m) {
//     const [, y, mo, d, h, mi, s] = m;
//     return { capturedAt: \`\${y}-\${mo}-\${d}T\${h}:\${mi}:\${s}\`, isCapturedAtTimeAccurate: true };
//   }
//
//   // Scan_2024-05-21.jpg -> date only, no reliable time of day
//   const dateOnly = filename.match(/(\\d{4}-\\d{2}-\\d{2})/);
//   if (dateOnly) {
//     return { capturedAt: dateOnly[1], isCapturedAtTimeAccurate: false, tags: ['scanned'] };
//   }

return null;
`;
